import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  Input,
  input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  WebchatBoxComponent,
  WebchatButtonComponent,
  DefaultPosition,
  TEMP_CONVERSATION_ID,
  TEMP_AI_ASSISTANT_PARTICIPANT_ID,
  TEMP_PARTICIPANT_ID,
} from '@galaxy/conversation/webchat-widget';
import { AIStatus, ConversationApiService, MetadataIdentifier, Metadata } from '@vendasta/conversation';
import { WebchatConfigService } from '../../services/webchat-config.service';
import { ChatService } from '../../services/chat.service';
import { getCookie, setCookie, initCookieProxy, VIEW_COOKIE_NAME } from '../../util/cookie';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import {
  CHAT_BUTTON_TOGGLE_TAG,
  SEND_MESSAGE_TAG,
  BOOK_APPOINTMENT_TAG,
  WIDGET_IMPRESSION_TAG,
} from '../../services/analytics/analytics.constants';
import { SentMessage } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { BookingAvailabilityOption, TRANSFER_TO_AGENT_METADATA_KEY } from '@galaxy/conversation/core';
import { BOOKING_PREFERRED_KEY } from '@galaxy/conversation/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service/src/snackbar.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';

// Webchat event type constants
const WEBCHAT_SEND_MESSAGE_EVENT = 'webchat:send-message';
const WEBCHAT_FILL_MESSAGE_EVENT = 'webchat:fill-message';
const WEBCHAT_URL_CHANGE_EVENT = 'webchat:url-change';
const WEBCHAT_MOBILE_CHANGE_EVENT = 'webchat:mobile-change';
const WEBCHAT_NARROW_VIEWPORT_CHANGE_EVENT = 'webchat:narrow-viewport-change';
const MAX_WIDTH = 432;
const MAX_HEIGHT = 600;
const MOBILE_CTA_HEIGHT = 120;
const CONTENT_CHROME = 104; // host padding (32) + container margin (16) + button (56)
const BUBBLE_SIZE = 100;

export function addUTMTrackingToURL(path: string, hostPageUrl?: string): string {
  if (!path) return path;
  try {
    const url = new URL(path);
    const referrer = hostPageUrl || window.location.href;
    const origin = hostPageUrl ? new URL(hostPageUrl).origin : window.location.origin;
    if (url.origin === origin) {
      return path;
    }
    url.searchParams.set('utm_source', 'webchat');
    url.searchParams.set('utm_medium', 'referral');
    url.searchParams.set('utm_referrer', referrer);
    return url.toString();
  } catch {
    return path;
  }
}

@Component({
  selector: 'app-webchat-root',
  imports: [CommonModule, TranslateModule, WebchatBoxComponent, WebchatButtonComponent],
  templateUrl: './webchat-root.component.html',
  styleUrls: ['./webchat-root.component.scss'],
  providers: [ConversationApiService],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.Emulated,
})
export class WebchatRootComponent implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly chatService = inject(ChatService);
  readonly configSvc = inject(WebchatConfigService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly parentOrigin: string;

  @ViewChild(WebchatBoxComponent) webchatBox?: WebchatBoxComponent;

  readonly conversation = this.chatService.conversation;
  readonly webchatColor: Signal<string | undefined> = computed(() => this.configSvc.config()?.color);
  readonly welcomeMessage: Signal<string | undefined> = computed(() => this.configSvc.config()?.welcomeMessage);
  readonly isEnabled: Signal<boolean> = computed(() => this.configSvc.config()?.isEnabled);
  readonly webchatTextColor: Signal<string | undefined> = computed(() => this.configSvc.config()?.textColor);
  readonly webchatAccentColor: Signal<string | undefined> = computed(() => this.configSvc.config()?.accentColor);
  readonly webchatAccentTextColor: Signal<string | undefined> = computed(
    () => this.configSvc.config()?.accentTextColor,
  );
  readonly isDefaultAssistantName = computed(() => this.configSvc.config()?.assistantName === 'Chat Receptionist');
  readonly assistantAvatarUrl: Signal<string | undefined> = computed(() => this.configSvc.config()?.assistantAvatarUrl);
  protected readonly position: Signal<'right' | 'left'> = computed(
    () => this.configSvc.config()?.position || DefaultPosition,
  );
  protected readonly showFooterContent = computed(() => this.configSvc.config()?.showFooterContent);
  protected readonly poweredBy = computed(() => ({
    name: this.configSvc.config()?.poweredByName,
    url: addUTMTrackingToURL(this.configSvc.config()?.poweredByUrl, this.configSvc.hostPageUrl()),
  }));
  protected readonly messageGroups = toSignal(this.chatService.messageGroups$, { initialValue: [] });
  protected readonly messages = toSignal(this.chatService.messages$);
  protected readonly currentParticipantId = this.authService.currentParticipantId;
  protected readonly isTransferred = computed(() =>
    this.conversation()?.metadata?.some((kv) => kv.key === TRANSFER_TO_AGENT_METADATA_KEY && kv.value === 'true') ??
    false,
  );
  private readonly aiParticipantId = computed(() => {
    const currentParticipantId = this.currentParticipantId();
    const msgs = this.messages() ?? [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const senderParticipantId = msgs[i].sender?.participantId;
      if (
        senderParticipantId &&
        senderParticipantId !== currentParticipantId &&
        senderParticipantId !== TEMP_AI_ASSISTANT_PARTICIPANT_ID &&
        senderParticipantId !== TEMP_PARTICIPANT_ID
      ) {
        return senderParticipantId;
      }
    }
    return null;
  });
  private readonly agentHasReplied = computed(() => {
    const aiParticipantId = this.aiParticipantId();
    const currentParticipantId = this.currentParticipantId();
    return (this.messages() ?? []).some((m) => {
      const senderParticipantId = m.sender?.participantId;
      return (
        senderParticipantId &&
        senderParticipantId !== currentParticipantId &&
        senderParticipantId !== aiParticipantId &&
        senderParticipantId !== TEMP_AI_ASSISTANT_PARTICIPANT_ID &&
        senderParticipantId !== TEMP_PARTICIPANT_ID
      );
    });
  });
  protected readonly isReplying = computed(() => {
    if (this.isTransferred() || this.agentHasReplied()) return false;

    const messages = this.messages();

    // Greeting window: greetingSent fires before addGreetingMessage is called.
    if (this.greetingSent() && !messages?.length) return true;

    // Support webchat: conversation exists but the AI's initial message hasn't arrived yet.
    if (this.conversation() && messages?.length === 0) return true;

    const latest = messages?.[0];

    // AI message still in-flight — keep the bubble until the response is complete.
    if (latest?.aiStatus === AIStatus.AI_RECEIVED || latest?.aiStatus === AIStatus.AI_THINKING) return true;

    return latest?.sender?.participantId === this.currentParticipantId();
  });
  private readonly enableGreetingMessage: Signal<boolean> = computed(
    () => this.configSvc.config()?.enableGreetingMessage,
  );

  readonly isChatOpen = signal<boolean | null>(null);
  readonly isMobile = signal(false);
  readonly isNarrowViewport = signal(false);
  readonly isMiniViewOpen = computed(
    () =>
      !this.embedded() &&
      this.isChatOpen() === null &&
      !this.conversation() &&
      (this.configSvc.config()?.showMobileCta || !this.isMobile()),
  );
  readonly showWebchat = computed(
    () =>
      this.embedded() ||
      this.isChatOpen() ||
      this.isMiniViewOpen() ||
      (this.isChatOpen() === null && this.conversation()),
  );

  protected readonly isCreatingConversation = computed(() => this.chatService.isCreatingConversation());
  protected readonly isMobileCta = computed(() => this.isMobile() && this.isMiniViewOpen() && !this.embedded());

  @HostBinding('id') id = 'webchat-root';

  @HostBinding('class.mobile-cta') get mobileCta() {
    return this.isMobileCta();
  }

  @HostBinding('class.position-left') get positionLeft() {
    return this.position() === 'left';
  }

  @HostBinding('class.embedded') get embeddedClass() {
    return this.embedded();
  }

  @Input() set widgetId(widgetId: string) {
    this.configSvc.loadWidget(widgetId);
    this.authService.loadSession(widgetId);
    this.chatService.openConversation();
  }

  @Input() set dataQueryParams(dataQueryParams: string) {
    this.chatService.setDataQueryParams(dataQueryParams);
  }

  @Input() set snapshotId(snapshotId: string) {
    this.chatService.setSnapshotId(snapshotId);
  }

  readonly embedded = input<boolean>(false);

  protected readonly canClose = computed(() => !this.embedded());
  protected readonly fullView = computed(
    () => this.embedded() || (!this.isMiniViewOpen() && (this.messages()?.length ?? 0) > 0),
  );
  protected readonly hideComposer = computed(() => this.isMiniViewOpen() && this.isMobile());

  private readonly greetingSent = signal(false);
  private readonly impressionSent = signal(false);
  private readonly openedByGreeting = signal(false);
  private contentResizeObserver: ResizeObserver | null = null;
  private viewportAnimating = false;
  private viewportAnimatingTimeout: ReturnType<typeof setTimeout> | null = null;
  private resizeCancel: (() => void) | null = null;
  private warnedAboutPinFallback = false;

  constructor() {
    const el = this.elementRef.nativeElement;
    this.parentOrigin = el.getAttribute('data-parent-origin') || '*';
    this.analyticsService.setParentOrigin(this.parentOrigin);
    initCookieProxy(el.getAttribute('data-cookies') || '', this.parentOrigin);

    const urlEncoded = el.getAttribute('parent-url');
    if (urlEncoded) this.configSvc.hostPageUrl.set(decodeURIComponent(urlEncoded));
    const hostname = el.getAttribute('parent-hostname');
    if (hostname) this.configSvc.hostPageHostname.set(hostname);

    effect(() => this.setViewCookie(this.isChatOpen()));
    effect(() => {
      if (this.greetingSent()) return;

      if (
        (!this.currentParticipantId() || !this.conversation()) &&
        this.messages()?.length === 0 &&
        this.isChatOpen() !== false &&
        (this.isChatOpen() || !this.isMobile()) &&
        this.enableGreetingMessage()
      ) {
        this.greetingSent.set(true);
        setTimeout(() => {
          if (this.isChatOpen() === false) return;
          this.chatService.addGreetingMessage(TEMP_CONVERSATION_ID);
          this.openedByGreeting.set(true);
          this.isChatOpen.set(true);
        }, 3000);
      }
    });

    effect(() => {
      if (this.configSvc.config()) {
        window.parent.postMessage(
          { type: 'webchat:ready', detail: { widgetId: this.configSvc.widgetId() } },
          this.parentOrigin,
        );
        if (!this.impressionSent()) {
          this.impressionSent.set(true);
          this.analyticsService.pushEvent(WIDGET_IMPRESSION_TAG);
        }
      }
    });

    effect(() => {
      if (!this.configSvc.config()) return;
      this.showWebchat();
      this.fullView();
      this.isMobile();
      this.isNarrowViewport();
      this.position();
      this.messages();

      this.resizeCancel?.();
      let canceled = false;
      this.resizeCancel = () => {
        canceled = true;
      };

      afterNextRender(
        () => {
          if (canceled) return;
          this.sendResize();
          this.observeContentResize();
        },
        { injector: this.injector },
      );
    });

    this.setupViewportAnimationDetection();
  }

  ngOnInit(): void {
    const el = this.elementRef.nativeElement;
    this.isMobile.set(el.getAttribute('data-is-mobile') === 'true');
    this.isNarrowViewport.set(el.getAttribute('data-is-narrow-viewport') === 'true');

    this.isChatOpen.set(this.getInitialOpenState());
    this.setupPostMessageListener();
    this.setupFocusNotification();
    this.setupLinkInterceptor();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handlePostMessage);
    this.contentResizeObserver?.disconnect();
    this.resizeCancel?.();
    if (this.viewportAnimatingTimeout) clearTimeout(this.viewportAnimatingTimeout);
  }

  expandChat(): void {
    if (this.isMiniViewOpen()) {
      this.toggleChat();
      return;
    }
    setTimeout(() => this.webchatBox?.focusInput({ preventScroll: true }), 0);
  }

  toggleChat(): void {
    this.openedByGreeting.set(false);
    this.isChatOpen.update((value) => {
      if (this.isMiniViewOpen()) {
        return this.isMobile();
      }
      return !value;
    });
    setTimeout(() => {
      this.webchatBox?.focusInput({ preventScroll: true });
      this.analyticsService.pushEvent(CHAT_BUTTON_TOGGLE_TAG);
    }, 0);
  }

  closeChat(): void {
    this.openedByGreeting.set(false);
    this.isChatOpen.set(false);
    setTimeout(() => {
      this.analyticsService.pushEvent(CHAT_BUTTON_TOGGLE_TAG);
    }, 0);
  }

  protected async sendMessage(msg: SentMessage): Promise<void> {
    if (!msg?.messageText) return;

    this.openedByGreeting.set(false);
    this.isChatOpen.set(true);

    await this.chatService.sendMessage(msg.messageText);

    setTimeout(() => {
      this.analyticsService.pushEvent(SEND_MESSAGE_TAG);
    }, 0);
  }

  async setPreferredBooking(booking: BookingAvailabilityOption) {
    try {
      await this.chatService.sendMessage(booking.displayText, [
        new Metadata({
          identifier: MetadataIdentifier.METADATA_IDENTIFIER_UNDEFINED,
          data: {
            [BOOKING_PREFERRED_KEY]: JSON.stringify(booking),
          },
        }),
      ]);
      setTimeout(() => {
        this.analyticsService.pushEvent(BOOK_APPOINTMENT_TAG);
      }, 0);
    } catch {
      this.snackbarService.openErrorSnack(this.translateService.instant('WEBCHAT.ERROR_SENDING_MESSAGE'));
    }
  }

  private sendResize(): void {
    const config = this.configSvc.config();
    if (!config || this.embedded() || (this.viewportAnimating && this.showWebchat())) return;

    const { width, height } = this.computeDimensions();

    window.parent.postMessage(
      {
        type: 'webchat:resize',
        detail: {
          widgetId: this.configSvc.widgetId(),
          width,
          height,
          position: this.position(),
          widgetTopSpace: config.widgetTopSpace || 0,
        },
      },
      this.parentOrigin,
    );
  }

  private computeDimensions(): { width: number; height: number } {
    if (this.isMobileCta()) {
      return { width: MAX_WIDTH, height: MOBILE_CTA_HEIGHT };
    }

    if (!this.showWebchat()) {
      return { width: BUBBLE_SIZE, height: BUBBLE_SIZE };
    }

    if (this.fullView()) {
      return { width: MAX_WIDTH, height: MAX_HEIGHT };
    }

    const boxHeight = this.elementRef.nativeElement.querySelector('webchat-box')?.offsetHeight ?? 0;
    return { width: MAX_WIDTH, height: Math.min(boxHeight + CONTENT_CHROME, MAX_HEIGHT) };
  }

  private observeContentResize(): void {
    const boxEl = this.elementRef.nativeElement.querySelector('webchat-box');
    if (boxEl && !this.contentResizeObserver) {
      this.contentResizeObserver = new ResizeObserver(() => this.sendResize());
      this.contentResizeObserver.observe(boxEl);
    } else if (!boxEl && this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }
  }

  private setViewCookie(isOpen: boolean | null): void {
    if (this.embedded()) return;
    if (isOpen == null) return;
    if (this.openedByGreeting()) return;
    setCookie(VIEW_COOKIE_NAME, this.isChatOpen() ? 'true' : 'false');
  }

  private getInitialOpenState(): boolean | null {
    if (this.embedded()) {
      return true; // Embedded mode defaults to open/is not able to be closed
    }
    const cookieValue = getCookie(VIEW_COOKIE_NAME);
    if (cookieValue == null || cookieValue === '') {
      return null;
    }
    if (cookieValue === 'true') {
      return this.isMobile() ? null : true;
    }
    return false;
  }

  private setupPostMessageListener(): void {
    window.addEventListener('message', this.handlePostMessage);
  }

  private setupFocusNotification(): void {
    if (this.embedded()) return;

    const notify = (focused: boolean) => {
      window.parent.postMessage({ type: 'webchat:input-focus', detail: { focused } }, this.parentOrigin);
    };

    const isTextInput = (el: EventTarget | null): boolean =>
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLInputElement ||
      (el instanceof HTMLElement && el.isContentEditable);

    this.elementRef.nativeElement.addEventListener('focusin', (e: FocusEvent) => {
      if (isTextInput(e.target)) notify(true);
    });
    this.elementRef.nativeElement.addEventListener('focusout', (e: FocusEvent) => {
      if (isTextInput(e.target)) notify(false);
    });
  }

  private setupLinkInterceptor(): void {
    this.elementRef.nativeElement.addEventListener('click', (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor?.href) return;

      event.preventDefault();
      if (anchor.target === '_blank') {
        window.open(anchor.href, '_blank', 'noopener,noreferrer');
      } else {
        setCookie(VIEW_COOKIE_NAME, 'false');
        setTimeout(() => {
          window.parent.location.href = anchor.href;
        });
      }
    });
  }

  private setupViewportAnimationDetection(): void {
    const vp = window.visualViewport;
    if (!vp) return;

    const onResize = () => {
      this.viewportAnimating = true;
      if (this.viewportAnimatingTimeout) clearTimeout(this.viewportAnimatingTimeout);
      this.viewportAnimatingTimeout = setTimeout(() => {
        this.viewportAnimating = false;
        this.sendResize();
      }, 300);
    };

    vp.addEventListener('resize', onResize, { passive: true });

    this.destroyRef.onDestroy(() => {
      vp.removeEventListener('resize', onResize);
    });
  }

  private handlePostMessage = async (event: MessageEvent) => {
    if (!this.isFromTrustedParent(event)) return;
    const data = event.data;
    if (!data?.type?.startsWith('webchat:')) return;

    if (data.type === WEBCHAT_MOBILE_CHANGE_EVENT) {
      this.isMobile.set(!!data.detail?.isMobile);
      return;
    }

    if (data.type === WEBCHAT_NARROW_VIEWPORT_CHANGE_EVENT) {
      this.isNarrowViewport.set(!!data.detail?.isNarrowViewport);
      return;
    }

    if (data.type === WEBCHAT_URL_CHANGE_EVENT) {
      const url = data.detail?.url;
      if (typeof url === 'string' && url.length > 0) {
        this.configSvc.hostPageUrl.set(url);
      }
      return;
    }

    if (data.type !== WEBCHAT_SEND_MESSAGE_EVENT && data.type !== WEBCHAT_FILL_MESSAGE_EVENT) return;

    const { widgetId, message } = data.detail;
    if (!widgetId || widgetId !== this.configSvc.widgetId()) return;

    this.isChatOpen.set(true);
    this.cdr.detectChanges();

    if (message) {
      if (data.type === WEBCHAT_SEND_MESSAGE_EVENT) {
        this.webchatBox?.onNewMessageSent();
        await this.sendMessage({ messageText: message });
      } else if (data.type === WEBCHAT_FILL_MESSAGE_EVENT) {
        this.webchatBox?.insertMessage(message, 'after');
      }
    }
  };

  // isFromTrustedParent enforces the origin pin captured at init
  // (data-parent-origin). When the host SDK runs on a page it cannot classify
  // (e.g. a sandboxed embed), it falls back to parentOrigin='*' and we accept
  // messages from any origin — but warn once per session so the missing pin
  // is visible in the host page's console.
  private isFromTrustedParent(event: MessageEvent): boolean {
    if (event.source !== window.parent) return false;
    if (this.parentOrigin === '*') {
      if (!this.warnedAboutPinFallback) {
        this.warnedAboutPinFallback = true;
        console.warn(
          'webchat: accepting parent→iframe messages without an origin pin (data-parent-origin="*"). ' +
            'Set a concrete parent origin on the host SDK to harden this boundary.',
        );
      }
      return true;
    }
    return event.origin === this.parentOrigin;
  }
}
